'use client'
import { Image } from '@/components'
import { SwiperNavButton, SwiperSlideStyle, SwiperStyle } from '@/features/home/components/product-slide'
import { Box, Divider, Stack, Typography } from '@mui/material'
import Link from 'next/link'
import React from 'react'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import { Navigation } from 'swiper/modules'

type props = {
  categories?: ICategoryMenu | null | undefined
}

export default function CategorySlider({ categories }: props) {
  if (!categories || categories.children.length === 0) {
    return null
  }
  const displayedCategories = categories?.children?.filter((category) => category?.product_count > 0)

  const displayedCount = displayedCategories?.length || 0

  return (
    <Stack gap={2}>
      <Box position={'relative'} className="swiper-category-slider">
        <SwiperStyle
          slidesPerView={'auto'}
          modules={[Navigation]}
          navigation={{
            prevEl: '.swiper-category-slider .swiper-btn-prev',
            nextEl: '.swiper-category-slider .swiper-btn-next',
          }}
          sx={{
            '& .swiper-wrapper': {
              justifyContent: {
                xs: displayedCount <= 4 ? 'center' : '',
                sm: 'center',
              },
            },
          }}
        >
          {displayedCategories?.map((category) => (
            <SwiperSlideStyle
              key={category.uid}
              itemsPerViewPc={8}
              itemsPerViewTablet={5}
              itemsPerViewMobile={3}
            >
              <Stack
                gap={0.5}
                position="relative"
                textAlign="center"
                sx={{
                  pointerEvents: category?.product_count === 0 ? 'none' : 'auto',
                  '&:hover p::after': {
                    width: '100%',
                    left: 0,
                  },
                }}
              >
                <Link
                  href={`/${category?.url_path || category?.url_key}`}
                  aria-label={category.name}
                  style={{ position: 'absolute', inset: 0, zIndex: 1 }}
                />

                <Box position="relative" borderRadius={2} overflow="hidden">
                  <Image
                    src={category?.image || ''}
                    alt={category?.name}
                    style={{
                      aspectRatio: '3/4',
                      display: 'block',
                      width: '100%',
                      backgroundColor: '#f5f5f5',
                    }}
                  />

                  {category?.product_count === 0 && (
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.01)',
                        backdropFilter: 'blur(12px)',
                        zIndex: 1,
                      }}
                    />
                  )}
                </Box>

                <Stack alignItems="center" mt={0.5}>
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 500,
                      position: 'relative',
                      paddingBottom: '1px',
                      fontSize: {
                        xs: '0.875rem',
                        md: '1rem',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 0,
                        height: '1px',
                        backgroundColor: 'primary.main',
                        transition: 'width 0.3s ease-in-out',
                      },
                    }}
                  >
                    {category?.name}
                  </Typography>
                  {category?.product_count === 0 && (
                    <Box
                      component="p"
                      sx={{
                        fontSize: '0.7rem',
                        color: 'warning.main',
                        fontWeight: 300,
                      }}
                    >
                      Sắp ra mắt
                    </Box>
                  )}
                </Stack>
              </Stack>
            </SwiperSlideStyle>
          ))}
        </SwiperStyle>
        <SwiperNavButton isLeft={true} className="swiper-btn-prev">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </SwiperNavButton>
        <SwiperNavButton isRight={true} className="swiper-btn-next">
          <ArrowForwardIosRoundedIcon fontSize="small" />
        </SwiperNavButton>
      </Box>
      <Divider />
    </Stack>
  )
}
